<template>
    <v-form ref="form" v-model="valid" :disabled="disabled">
        <!-- TODO [P4] workaround -->
        <v-text-field v-show="false" />
        <v-row>
            <v-col cols="12">
                <span>
                    <span class="text-h6 text-capitalize">
                        {{ `${$t(`transfers.${pointType === 'departure' ? 'from' : 'to'}`)}: ` }}
                    </span>
                    <v-icon class="me-2">mdi-airplane-takeoff</v-icon>
                    <span class="text-subtitle-2">
                        {{ formTitle }}
                    </span>
                </span>
            </v-col>
        </v-row>
        <v-row>
            <v-col v-if="!isProductFieldDisabled('POINT_FLIGHT_NUMBER')" cols="12" sm="4" md="3" lg="2">
                <v-text-field
                    v-model="flightNumber"
                    :rules="flightNumberRules"
                    :label="productFieldLabel('transfers.flight_number', 'POINT_FLIGHT_NUMBER')"
                />
            </v-col>
            <v-col v-if="!isProductFieldDisabled('POINT_AIRPORT')" cols="12" sm="8" md="6" lg="4">
                <airports-search-input
                    v-model="otherAirportCode"
                    :label="
                        pointType === 'arrival'
                            ? productFieldLabel('transfers.departure_to_airport', 'POINT_AIRPORT')
                            : productFieldLabel('transfers.arriving_from_airport', 'POINT_AIRPORT')
                    "
                    :rules="airportRules"
                />
            </v-col>
            <v-col v-if="!isProductFieldDisabled('POINT_TERMINAL')" cols="12" sm="4" md="3" lg="2">
                <v-text-field v-model="terminal" :label="productFieldLabel('transfers.terminal', 'POINT_TERMINAL')" />
            </v-col>
            <template v-if="!isProductFieldDisabled('POINT_FLIGHT_TIME')">
                <v-col cols="12" sm="8" md="6" lg="6" xl="4" class="d-flex">
                    <date-picker
                        v-model="date"
                        :label="productFieldLabel('date', 'POINT_FLIGHT_TIME')"
                        :rules="flightDateRules"
                        :min="minDate"
                        :max="maxDate"
                        content-class="date-picker me-4"
                    />
                    <time-field
                        v-model="time"
                        :label="productFieldLabel('time', 'POINT_FLIGHT_TIME')"
                        :rules="flightTimeRules"
                        content-class="time-field"
                    />
                </v-col>
            </template>
        </v-row>
    </v-form>
</template>

<script lang="ts">
    import {Component, mixins, Prop, VModel} from 'vue-facing-decorator'
    import AirportsSearchInput from '~/components/snippets/AirportsSearchInput.vue'
    import TimeField from '@/components/snippets/forms/TimeField.vue'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import {TransferFormMixin} from '@/components/transfers/mixins/TransferFormMixin.vue'
    import {ProductBookingFieldsMixin} from '@/components/booking/mixins/ProductBookingFieldsMixin.vue'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'

    @Component({
        components: {TimeField, AirportsSearchInput, DatePicker},
    })
    export class TransferAirportForm extends mixins(
        TransferFormMixin,
        ProductBookingFieldsMixin,
        FormValidationRulesMixin
    ) {
        @VModel({default: false}) valid
        @Prop() pointType
        @Prop() disabled
        @Prop() product
        @Prop() searchRequest
        @Prop() flightService
        @Prop() offer

        mounted() {
            this.initData()
        }

        initData() {
            const date = this.$dateFns.parseISO(this.date)
            if (this.isReturnTransfer) {
                this.setProp('dateTime', this.offer.date + 'T00:00')
            } else if (
                !this.date ||
                this.$dateFns.isAfter(date, this.$dateFns.parseISO(this.maxDate)) ||
                this.$dateFns.isBefore(date, this.$dateFns.parseISO(this.minDate))
            ) {
                this.setProp('dateTime', this.searchRequest.dateTime)
            }

            if (
                this.product.offers?.length &&
                this.product.offers[0].departureInfo.type === 'AIRPORT' &&
                this.pointType === 'departure' &&
                this.flightService
            ) {
                const {flightNumber, departureAirportCode, departureTerminal} =
                    this.flightService.serviceDetails[0].itineraries[0].flightSegments[0]
                this.setProp('flightNumber', flightNumber)
                this.setProp('otherAirportCode', departureAirportCode)
                this.setProp('terminal', departureTerminal)
            }
            if (
                this.product.offers?.length &&
                this.product.offers[0].arrivalInfo.type === 'AIRPORT' &&
                this.pointType === 'arrival' &&
                this.flightService &&
                this.flightService.serviceDetails[0].itineraries[1]
            ) {
                const {flightNumber, arrivalAirportCode, arrivalTerminal} =
                    this.flightService.serviceDetails[0].itineraries[1].flightSegments[0]
                this.setProp('flightNumber', flightNumber)
                this.setProp('otherAirportCode', arrivalAirportCode)
                this.setProp('terminal', arrivalTerminal)
            }
        }

        splitDateTime() {
            let dateTime = this.getProp('dateTime')
            if (dateTime) {
                dateTime = dateTime.split('T')
            } else {
                dateTime = []
            }
            return dateTime
        }

        get flightDateRules() {
            const rules = []
            if (this.isProductFieldMandatory('POINT_FLIGHT_TIME') && !this.disableRequiredRule) {
                rules.push(this.requiredRule('date', 'POINT_FLIGHT_TIME'))
            }
            return rules
        }

        get flightTimeRules() {
            const rules = []
            if (this.isProductFieldMandatory('POINT_FLIGHT_TIME') && !this.disableRequiredRule) {
                rules.push(this.requiredRule('time', 'POINT_FLIGHT_TIME'))
            }
            return rules
        }

        get airportRules() {
            const rules = []
            if (this.isProductFieldMandatory('POINT_AIRPORT') && !this.disableRequiredRule) {
                rules.push(
                    this.pointType === 'arrival'
                        ? this.requiredRule('transfers.departure_to_airport', 'POINT_AIRPORT')
                        : this.requiredRule('transfers.arriving_from_airport', 'POINT_AIRPORT')
                )
            }
            return rules
        }

        get flightNumberRules() {
            const rules = []
            if (this.isProductFieldMandatory('POINT_FLIGHT_NUMBER') && !this.disableRequiredRule) {
                rules.push(this.requiredRule('transfers.flight_number', 'POINT_FLIGHT_NUMBER'))
            }
            if (!this.$conf.public.disableFlightNumberValidation) {
                rules.push(
                    v =>
                        !v ||
                        /^([A-Z]{3}|[A-Z\d]{2})\s?(\d{1,4})$/.test(v) ||
                        `${this.$t('transfers.flight_number_error')}`
                )
            }
            return rules
        }

        get flightNumber() {
            return this.getProp('flightNumber')
        }

        set flightNumber(val) {
            this.setProp('flightNumber', val.toUpperCase())
        }

        get date() {
            const dateTime = this.getProp('dateTime')
            if (!dateTime) return dateTime
            return dateTime.split('T')[0]
        }

        set date(val) {
            const dateTime = this.splitDateTime()
            dateTime[0] = val
            this.setProp('dateTime', dateTime.join('T'))
        }

        get time() {
            const dateTime = this.getProp('dateTime')
            if (!dateTime) return null
            return dateTime.split('T')[1]
        }

        set time(val) {
            const dateTime = this.splitDateTime()
            dateTime[1] = val
            this.setProp('dateTime', dateTime.join('T'))
        }

        get otherAirportCode() {
            return this.getProp('otherAirportCode')
        }

        set otherAirportCode(val) {
            this.setProp('otherAirportCode', val)
        }

        get ownProduct() {
            return transfersRuntimeStore.ownProduct(this.product)
        }

        get minDate() {
            if (this.pointType === 'arrival') {
                return this.searchRequest.dateTime
            } else {
                return this.$dateFns.format(
                    this.$dateFns.subDays(this.$dateFns.parseISO(this.searchRequest.dateTime), 1)
                )
            }
        }

        get maxDate() {
            if (this.isReturnTransfer) {
                return this.$dateFns.format(this.$dateFns.addDays(this.$dateFns.parseISO(this.offer.date), 1))
            }
            if (this.pointType === 'arrival') {
                return this.$dateFns.format(
                    this.$dateFns.addDays(this.$dateFns.parseISO(this.searchRequest.dateTime), 1)
                )
            } else {
                return this.searchRequest.dateTime
            }
        }

        get terminal() {
            return this.getProp('terminal')
        }

        set terminal(value) {
            this.setProp('terminal', value)
        }
    }

    export default TransferAirportForm
</script>
